/*
 * bc-presence.js — Beyond Carat live-visitor presence beacon (slice 3b).
 * ---------------------------------------------------------------------------
 * Split out of snippets/bc-presence.liquid (2026-07-02) so the heartbeat body
 * is a cached, deferred asset instead of inline JS. Config (endpoint + cadence)
 * is handed across from the slim snippet via window.__bcPresenceCfg.
 *
 * WHAT IT DOES
 * ------------
 * Posts a tiny "I'm here" heartbeat to the Cloudflare Worker at
 * https://events.beyondcarat.com/presence every ~30s while the tab is visible.
 * The CRM dossier consumes this so sales reps see which customers are live.
 *   - Reads the existing `bc_aid` cookie (set by datalayer-init.liquid).
 *   - NEVER mints a cookie — silently no-ops if bc_aid is missing / not a UUID.
 *   - Pauses on tab hidden; resumes on tab visible. One final beacon on pagehide.
 *   - Best-effort — wrapped in try/catch so the storefront never sees an error.
 *
 * DATA-SAVER GATE (2026-07-02)
 * ----------------------------
 * When navigator.connection.saveData is true we skip presence entirely — no
 * first beacon, no heartbeat. Live-presence is a nice-to-have for reps and not
 * worth spending a data-saver visitor's metered bytes every 30s.
 *
 * MOBILE BATTERY NOTE
 * -------------------
 * 30s cadence must stay <= the Lobby DO's STALE_AFTER_MS (30_000, workers/edge/
 * src/lobby.ts) or the dossier evicts live visitors between beats.
 */
(function () {
  'use strict';

  var cfg = window.__bcPresenceCfg || {};
  // Configuration (swap PRESENCE_URL in the snippet to migrate endpoints).
  var ENDPOINTS = {
    presence: cfg.url || 'https://events.beyondcarat.com/presence'
  };
  var PRESENCE_URL = ENDPOINTS.presence;
  var HEARTBEAT_MS = cfg.heartbeatMs || 30000;
  var DEBUG        = !!cfg.debug;
  var UUID_RE      = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  // Module state
  var bcAid     = null;   // bc_aid cookie value (validated UUID)
  var sessionId = null;   // sessionStorage bc_sess
  var timerId   = null;   // setInterval handle
  var stopped   = false;  // permanent stop (e.g. final unload beacon fired)

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[bc-presence]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return m ? decodeURIComponent(m[2]) : null;
    } catch (e) { return null; }
  }

  function readSessionId() {
    // Reuse the established session id when available. If storage is blocked,
    // omit it — the Worker contract makes session_id optional.
    try { return sessionStorage.getItem('bc_sess') || null; } catch (e) { return null; }
  }

  function buildPayload(closing) {
    var p = {
      bc_aid:     bcAid,
      session_id: sessionId || readSessionId() || undefined,
      path:       location.pathname,
      page_title: document.title,
      ts_ms:      Date.now()
    };
    // `closing: true` tells the Worker this is the final beacon for this tab —
    // the DO drops the bc_aid from the presence map and broadcasts an immediate
    // "presence_end" so the rep dossier flips to "Idle" within ~1s.
    if (closing) p.closing = true;
    return p;
  }

  function send(payload) {
    try {
      var body = JSON.stringify(payload);
      // Content-Type: text/plain to dodge the CORS preflight that
      // application/json forces — sendBeacon silently drops the POST after a
      // successful OPTIONS in several browsers (Chrome especially). The Worker
      // calls request.json() regardless of Content-Type, so it still parses.
      try {
        if (navigator.sendBeacon) {
          var blob = new Blob([body], { type: 'text/plain' });
          if (navigator.sendBeacon(PRESENCE_URL, blob)) { log('beacon sent', payload.path); return; }
        }
      } catch (e) { /* fall through to fetch */ }

      // Fallback: keepalive fetch so it can complete during unload.
      fetch(PRESENCE_URL, {
        method:    'POST',
        body:      body,
        headers:   { 'Content-Type': 'text/plain' },
        keepalive: true,
        mode:      'cors',
        credentials: 'include'
      }).catch(function () { /* silent — presence is best-effort */ });
      log('fetch sent', payload.path);
    } catch (e) {
      log('send failed', e);
    }
  }

  function tick() {
    if (stopped) return;
    if (document.visibilityState !== 'visible') return;
    send(buildPayload());
  }

  function startHeartbeat() {
    if (stopped || timerId !== null) return;
    timerId = setInterval(tick, HEARTBEAT_MS);
    log('heartbeat started');
  }

  function stopHeartbeat() {
    if (timerId !== null) {
      clearInterval(timerId);
      timerId = null;
      log('heartbeat paused');
    }
  }

  function init() {
    try {
      // Data-saver gate: skip presence entirely on metered / save-data connections.
      try {
        var conn = navigator.connection;
        if (conn && conn.saveData) { log('saveData on; skipping presence'); return; }
      } catch (e) { /* Network Information API absent — proceed */ }

      var raw = getCookie('bc_aid');
      if (!raw || !UUID_RE.test(raw)) {
        // No identity yet — silently no-op. We do NOT mint cookies here.
        log('no valid bc_aid cookie; skipping');
        return;
      }
      bcAid     = raw;
      sessionId = readSessionId();

      // First beacon ASAP so the dossier turns "live" without waiting 30s.
      if (document.visibilityState === 'visible') send(buildPayload());

      startHeartbeat();

      document.addEventListener('visibilitychange', function () {
        try {
          if (document.visibilityState === 'visible') {
            // Immediate beacon on return so the dossier flips back to "live".
            send(buildPayload());
            startHeartbeat();
          } else {
            stopHeartbeat();
          }
        } catch (e) { /* silent */ }
      });

      // Final beacon on unload. `pagehide` is the modern, BFCache-friendly
      // signal; `beforeunload` covers the long-tail of older browsers.
      var fireFinal = function () {
        if (stopped) return;
        stopped = true;
        stopHeartbeat();
        try { send(buildPayload(true)); } catch (e) { /* silent */ }
      };
      window.addEventListener('pagehide',     fireFinal);
      window.addEventListener('beforeunload', fireFinal);

      log('initialised', { bc_aid: bcAid, session_id: sessionId });
    } catch (e) {
      log('init failed', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
